import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  MdPersonAdd, MdDownload, MdPhonelinkRing, MdDelete,
  MdArchive, MdUnarchive, MdSearch, MdContacts,
} from 'react-icons/md';
import client from '../api/client';
import {
  bulkEntries, createEntry, deleteEntry, exportVcf, getPreferences, importCommit, listEntries,
  listGroups, listPeople, recoverNumbers, setPreferences, updateEntry,
} from '../api/addressBook';
import { messaggioErrore } from '../utils/apiError';
import {
  Badge, Button, Checkbox, ConfirmDialog, DialogBody, DialogFooter, EmptyState,
  Field, FilterChip, IconButton, Modal, PageHeader, Pagination, SkeletonList, Switch, Tabs, Toolbar,
} from '../components/ui';
import CandidatesPanel from '../components/address/CandidatesPanel';
import MergeDialog from '../components/address/MergeDialog';
import PersonDrawer from '../components/address/PersonDrawer';
import GroupsPanel from '../components/address/GroupsPanel';
import GroupMembersDrawer from '../components/address/GroupMembersDrawer';

const FILTRI = [
  { id: 'person', etichetta: 'Persone', params: { kind: 'person' } },
  { id: 'group', etichetta: 'Gruppi WhatsApp', params: { kind: 'group' } },
  { id: 'senza', etichetta: 'Senza numero', params: { has_phone: '0' } },
  { id: 'recenti', etichetta: 'Aggiunti di recente', params: { ordering: 'imported' } },
  { id: 'archiviati', etichetta: 'Archiviati', params: { archived: '1' } },
];

const ORIGINE = {
  whatsapp: { testo: 'da WhatsApp', tone: 'success' },
  manual: { testo: 'a mano', tone: 'info' },
  none: { testo: 'non disponibile', tone: 'warning' },
};

export default function AddressBookPage() {
  const [connections, setConnections] = useState([]);
  const [connection, setConnection] = useState('');
  const [vista, setVista] = useState('rubrica');
  const [entries, setEntries] = useState({ results: [], count: 0 });
  const [people, setPeople] = useState([]);
  const [groups, setGroups] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');

  const [ricerca, setRicerca] = useState('');
  const [ricercaDebounce, setRicercaDebounce] = useState('');
  const [filtri, setFiltri] = useState(['person']);
  const [pagina, setPagina] = useState(1);
  const [selezione, setSelezione] = useState([]);

  const [autoAdd, setAutoAdd] = useState(false);
  const [bozza, setBozza] = useState(null);
  const [personaAperta, setPersonaAperta] = useState(null);
  const [gruppoAperto, setGruppoAperto] = useState(null);
  const [daUnire, setDaUnire] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setRicercaDebounce(ricerca); setPagina(1); }, 300);
    return () => clearTimeout(t);
  }, [ricerca]);

  useEffect(() => {
    client.get('/v1/sessions/')
      .then(({ data }) => {
        const attive = data.filter(s => !s.is_deleted);
        setConnections(attive);
        setConnection(old => old || (attive[0] ? String(attive[0].id) : ''));
      })
      .catch(e => setErrore(messaggioErrore(e)));
    getPreferences().then(d => setAutoAdd(d.auto_add)).catch(() => {});
  }, []);

  const parametri = useMemo(() => {
    const base = { page: pagina, search: ricercaDebounce || undefined };
    if (connection) base.connection = connection;
    const attivi = FILTRI.filter(f => filtri.includes(f.id));
    attivi.forEach(f => Object.assign(base, f.params));
    const tipi = ['person', 'group'].filter(t => filtri.includes(t));
    if (tipi.length) base.kind = tipi.join(',');
    return base;
  }, [pagina, ricercaDebounce, connection, filtri]);

  const ricarica = useCallback(async () => {
    setCaricamento(true); setErrore('');
    try {
      const [elenco, persone, gruppi] = await Promise.all([
        listEntries(parametri), listPeople({ page_size: 200 }), listGroups(),
      ]);
      setEntries(elenco);
      setPeople(persone.results || persone);
      setGroups(gruppi.results || gruppi);
      setSelezione([]);
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setCaricamento(false);
    }
  }, [parametri]);

  useEffect(() => { ricarica(); }, [ricarica]);

  const esegui = async (azione, messaggio) => {
    setBusy(true); setErrore('');
    try {
      const esito = await azione();
      if (messaggio) toast.success(typeof messaggio === 'function' ? messaggio(esito) : messaggio);
      await ricarica();
      return esito;
    } catch (e) {
      const testo = messaggioErrore(e);
      setErrore(testo); toast.error(testo);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const righe = entries.results || [];
  const tutteSelezionate = righe.length > 0 && selezione.length === righe.length;
  const commutaFiltro = id => {
    setFiltri(filtri.includes(id) ? filtri.filter(f => f !== id) : [...filtri, id]);
    setPagina(1);
  };

  // Le identità importate si possono ricreare da chat e mittenti: un'eliminazione
  // per sbaglio si annulla riaggiungendole, senza passare dal backup.
  const eliminaConAnnulla = async () => {
    const identificativi = righe.filter(r => selezione.includes(r.id) || daEliminare?.includes(r.id))
      .map(r => r.whatsapp_id);
    const quanti = daEliminare.length;
    await esegui(async () => {
      if (quanti === 1) await deleteEntry(daEliminare[0]);
      else await bulkEntries({ action: 'delete', ids: daEliminare });
      setDaEliminare(null);
    });
    toast.info(
      <span className="flex items-center gap-3">
        {quanti} eliminati
        <button type="button" className="font-semibold underline"
          onClick={() => esegui(
            () => importCommit({ connection: Number(connection), whatsapp_ids: identificativi }),
            'Eliminazione annullata.')}>
          Annulla
        </button>
      </span>,
      { autoClose: 8000 });
  };

  const scaricaVcf = () => esegui(async () => {
    const blob = await exportVcf(Number(connection));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'rubrica.vcf'; a.click();
    URL.revokeObjectURL(url);
  });

  const salvaContatto = () => esegui(async () => {
    const corpo = {
      connection: Number(connection), name: bozza.name, short_name: bozza.short_name,
      category: bozza.category, phone: bozza.phone, email: bozza.email, notes: bozza.notes,
    };
    if (bozza.id) await updateEntry(bozza.id, corpo); else await createEntry(corpo);
    setBozza(null);
  }, 'Contatto salvato.');

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <PageHeader
        title="Rubrica"
        subtitle="Una persona, tutte le sue identità WhatsApp. I gruppi rubrica contengono persone: l'account con cui inviare si sceglie al momento dell'invio."
        actions={(
          <>
            <Button icon={MdPhonelinkRing} disabled={!connection} loading={busy}
              onClick={() => esegui(() => recoverNumbers({ connection: Number(connection) }), e => e.notice)}>
              Recupera numeri
            </Button>
            <Button variant="primary" icon={MdPersonAdd} disabled={!connection}
              onClick={() => setBozza({ name: '', short_name: '', category: '', phone: '', email: '', notes: '' })}>
              Nuovo contatto
            </Button>
          </>
        )}
      />

      {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}

      <label className="flex items-center gap-2 text-sm">
        <span className="shrink-0 text-muted">Account WhatsApp</span>
        <select aria-label="Account WhatsApp" value={connection}
          onChange={e => { setConnection(e.target.value); setPagina(1); }}
          className="rounded border border-line bg-surface px-2 py-2 text-sm">
          {connections.map(s => <option key={s.id} value={s.id}>{s.phone_number || s.session_id}</option>)}
        </select>
      </label>

      <Tabs
        label="Sezioni della rubrica"
        value={vista}
        onChange={setVista}
        tabs={[
          { value: 'rubrica', label: `In rubrica${entries.count ? ` (${entries.count})` : ''}` },
          { value: 'candidati', label: 'Da aggiungere' },
        ]}
      />

      {vista === 'candidati' ? (
        <CandidatesPanel connection={connection}
          onImported={esito => { toast.success(`${esito.added} contatti aggiunti alla rubrica.`); ricarica(); }} />
      ) : (
        <>
          <Toolbar className="flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <MdSearch size={18} aria-hidden className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
              <input aria-label="Cerca in rubrica" placeholder="Cerca nome, numero o categoria"
                value={ricerca} onChange={e => setRicerca(e.target.value)}
                className="w-full rounded border border-line bg-surface py-2 pl-8 pr-3 text-base md:text-sm" />
            </div>
            <Button icon={MdDownload} size="sm" onClick={scaricaVcf} disabled={!connection}>Esporta .vcf</Button>
          </Toolbar>

          <div className="flex flex-wrap gap-2">
            {FILTRI.map(f => (
              <FilterChip key={f.id} active={filtri.includes(f.id)} onClick={() => commutaFiltro(f.id)}>
                {f.etichetta}
              </FilterChip>
            ))}
          </div>

          <Switch label="Aggiungi automaticamente i nuovi interlocutori"
            hint="Ogni persona che ti scrive entra in rubrica da sola."
            checked={autoAdd}
            onChange={valore => esegui(async () => { await setPreferences({ auto_add: valore }); setAutoAdd(valore); })} />

          {selezione.length > 0 && (
            <div className="sticky bottom-2 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-brand-500/40 bg-brand-50 p-2 shadow-card">
              <strong className="text-sm text-brand-700">{selezione.length} selezionati</strong>
              <span className="flex-1" />
              <Button size="sm" icon={MdArchive}
                onClick={() => esegui(() => bulkEntries({ action: 'archive', ids: selezione }), 'Contatti archiviati.')}>
                Archivia
              </Button>
              <Button size="sm" icon={MdUnarchive}
                onClick={() => esegui(() => bulkEntries({ action: 'restore', ids: selezione }), 'Contatti ripristinati.')}>
                Ripristina
              </Button>
              <Button size="sm" variant="danger" icon={MdDelete} onClick={() => setDaEliminare(selezione)}>
                Elimina
              </Button>
            </div>
          )}

          {caricamento ? <SkeletonList rows={6} /> : righe.length === 0 ? (
            <EmptyState icon={MdContacts} title="Nessun contatto con questi filtri"
              hint="Apri “Da aggiungere” per pescare dalle chat solo le persone che ti servono."
              action={<Button variant="primary" onClick={() => setVista('candidati')}>Vai a “Da aggiungere”</Button>} />
          ) : (
            <div className="overflow-hidden rounded-lg border border-line bg-surface">
              <div className="flex items-center gap-3 border-b border-line bg-surface-2 px-3 py-2">
                <Checkbox label={<span className="text-xs text-muted">Seleziona tutti in pagina</span>}
                  checked={tutteSelezionate}
                  onChange={e => setSelezione(e.target.checked ? righe.map(r => r.id) : [])} />
              </div>
              <ul>
                {righe.map(riga => {
                  const origine = ORIGINE[riga.phone_source] || ORIGINE.none;
                  const persona = people.find(p => p.id === riga.person);
                  return (
                    <li key={riga.id} className="flex items-start gap-3 border-b border-line px-3 py-2.5 last:border-0">
                      <input type="checkbox" aria-label={`Seleziona ${riga.name}`}
                        className="mt-1 h-5 w-5 shrink-0 rounded border-line accent-[rgb(var(--brand-500))]"
                        checked={selezione.includes(riga.id)}
                        onChange={e => setSelezione(e.target.checked
                          ? [...selezione, riga.id] : selezione.filter(x => x !== riga.id))} />
                      <button type="button" className="min-w-0 flex-1 text-left"
                        onClick={() => (riga.kind === 'group'
                          ? setGruppoAperto(riga)
                          : persona && setPersonaAperta(persona))}>
                        <p className="truncate text-sm font-medium text-ink">{riga.person_name || riga.name}</p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                          <span className="tabular-nums">{riga.phone || 'Numero non disponibile'}</span>
                          <Badge tone={origine.tone}>{origine.testo}</Badge>
                          {riga.kind === 'group'
                            ? <Badge tone="info">Gruppo WhatsApp · vedi i membri</Badge>
                            : null}
                          {riga.category && <Badge>{riga.category}</Badge>}
                          {riga.archived && <Badge>Archiviato</Badge>}
                        </p>
                      </button>
                      <IconButton icon={MdDelete} label={`Rimuovi ${riga.name} dalla rubrica`} tone="danger"
                        onClick={() => setDaEliminare([riga.id])} />
                    </li>
                  );
                })}
              </ul>
              <div className="border-t border-line px-3 py-2">
                <Pagination page={pagina} pageCount={Math.max(1, Math.ceil(entries.count / 50))}
                  total={entries.count} etichetta="contatti" onChange={setPagina} />
              </div>
            </div>
          )}

          <GroupsPanel groups={groups} people={people} connection={connection}
            connections={connections} onChanged={ricarica} />
        </>
      )}

      <GroupMembersDrawer entry={gruppoAperto} open={!!gruppoAperto} onClose={() => setGruppoAperto(null)}
        onImported={esito => {
          toast.success(`${esito.added} persone aggiunte dalla rubrica del gruppo.`);
          ricarica();
        }} />

      <PersonDrawer person={personaAperta} open={!!personaAperta} onClose={() => setPersonaAperta(null)}
        connections={connections} onChanged={ricarica}
        onEditIdentity={identita => { setPersonaAperta(null); setBozza({ ...identita, name: identita.name || '' }); }}
        onMerge={persona => { setPersonaAperta(null); setDaUnire(persona); }} />

      <MergeDialog open={!!daUnire} target={daUnire} people={people}
        onClose={() => setDaUnire(null)}
        onMerged={esito => { toast.success(esito.notice); setDaUnire(null); ricarica(); }} />

      <Modal open={!!bozza} onClose={() => setBozza(null)} size="md"
        label={bozza?.id ? 'Modifica identità' : 'Nuovo contatto'}
        description={bozza?.whatsapp_id
          ? `Identità WhatsApp: ${bozza.whatsapp_id} — non cambia registrando il numero.`
          : 'Serve un numero internazionale: sarà la sua identità WhatsApp.'}>
        <DialogBody className="space-y-3">
          {[['name', 'Nome', true], ['short_name', 'Nome breve', false], ['category', 'Categoria', false],
            ['phone', 'Numero internazionale', !bozza?.id], ['email', 'Email', false]].map(([campo, etichetta, obbligatorio]) => (
            <Field key={campo} label={etichetta} required={obbligatorio}
              type={campo === 'email' ? 'email' : 'text'}
              value={bozza?.[campo] || ''}
              onChange={e => setBozza({ ...bozza, [campo]: e.target.value })} />
          ))}
          <Field label="Note">
            {props => (
              <textarea {...props} rows={3} value={bozza?.notes || ''}
                onChange={e => setBozza({ ...bozza, notes: e.target.value })} />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setBozza(null)}>Annulla</Button>
          <Button variant="primary" loading={busy} onClick={salvaContatto}>Salva</Button>
        </DialogFooter>
      </Modal>

      <ConfirmDialog open={!!daEliminare} onClose={() => setDaEliminare(null)} danger loading={busy}
        title={daEliminare?.length > 1 ? `Eliminare ${daEliminare.length} contatti?` : 'Eliminare il contatto?'}
        message="Vengono tolti dalla rubrica, con le note e le categorie che hai scritto. Chat e messaggi restano. Avrai qualche secondo per annullare."
        confirmLabel="Elimina"
        onConfirm={eliminaConAnnulla} />
    </div>
  );
}
